import React, { useState } from 'react';
import { CircleCheck, Mail, Send } from 'lucide-react';
import '../../assets/styles/ContactUs.css';
import '../../assets/styles/SectionBand.css';
import '../../assets/styles/ContactPage.css';
import Footer, { SUPPORT_EMAIL } from '../Footer';
import SectionEyebrow from '../common/SectionEyebrow';
import FormField from '../common/FormField';
import { useLanguage } from '../../assets/js/LanguageContext';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_MESSAGE = 5000;
const EMPTY = { fullName: '', email: '', message: '', website: '' };

function ContactUs() {
  const { t } = useLanguage();
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (field) => (event) => {
    const { value } = event.target;
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined, form: undefined }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = {};
    if (!values.fullName.trim()) nextErrors.fullName = t('contactus_error_name');
    if (!values.email.trim()) nextErrors.email = t('contactus_error_email_required');
    else if (!EMAIL_RE.test(values.email.trim())) nextErrors.email = t('contactus_error_email_invalid');
    if (!values.message.trim()) nextErrors.message = t('contactus_error_message');
    else if (values.message.trim().length > MAX_MESSAGE) nextErrors.message = t('contactus_error_message_long').replace('{max}', MAX_MESSAGE);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSending(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/send-pin-email-contacto`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values)
      });
      if (response.ok) {
        setSent(true);
        setValues(EMPTY);
        return;
      }
      const data = await response.json().catch(() => ({}));
      if (data.field === 'email') setErrors({ email: t('contactus_error_email_invalid') });
      else setErrors({ form: t(response.status === 429 ? 'contactus_error_too_many' : 'contactus_error_send') });
    } catch (error) {
      setErrors({ form: t('contactus_error_send') });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="brand-page-stack contact-page">
      <section className="section-band">
        <div className="section-band__inner tp-form-layout">
          <div className="tp-form-layout__intro">
            <h1 className="tp-sr-only">{t('contactus_title')}</h1>
            <div aria-hidden="true">
              <SectionEyebrow>{t('contactus_title')}</SectionEyebrow>
            </div>
            <p className="tp-form-layout__statement">{t('contactus_intro1')}</p>
            <p className="section-band__lead">{t('contactus_intro2')}</p>

            <a href={`mailto:${SUPPORT_EMAIL}`} className="tp-contact__mail">
              <span className="tp-contact__mail-icon" aria-hidden="true">
                <Mail size={20} strokeWidth={1.75} />
              </span>
              <span className="tp-contact__mail-text">
                <span className="tp-contact__mail-label">{t('contactus_email_label')}</span>
                <span className="tp-contact__mail-address">{SUPPORT_EMAIL}</span>
              </span>
            </a>
          </div>

          {sent ? (
            <div className="tp-form tp-contact__sent" role="status">
              <span className="tp-contact__sent-icon" aria-hidden="true">
                <CircleCheck size={28} strokeWidth={1.75} />
              </span>
              <h2 className="tp-contact__sent-title">{t('contactus_sent_title')}</h2>
              <p className="tp-contact__sent-text">{t('contactus_sent_text')}</p>
              <button type="button" className="btn btn--outline" onClick={() => setSent(false)}>
                {t('contactus_send_another')}
              </button>
            </div>
          ) : (
            <form className="tp-form" onSubmit={handleSubmit} noValidate>
              <FormField
                id="fullName"
                name="fullName"
                label={t('contactus_label_name')}
                autoComplete="name"
                placeholder={t('contactus_placeholder_name')}
                value={values.fullName}
                onChange={update('fullName')}
                error={errors.fullName}
                maxLength={120}
                required
              />
              <FormField
                id="email"
                name="email"
                type="email"
                label={t('contactus_label_email')}
                autoComplete="email"
                placeholder={t('contactus_placeholder_email')}
                value={values.email}
                onChange={update('email')}
                error={errors.email}
                required
              />
              <FormField
                id="message"
                name="message"
                as="textarea"
                rows={6}
                label={t('contactus_label_message')}
                placeholder={t('contactus_placeholder_message')}
                value={values.message}
                onChange={update('message')}
                error={errors.message}
                required
              />
              {/* Campo trampa para bots: oculto para personas y lectores de pantalla */}
              <input
                type="text"
                name="website"
                className="tp-contact__trap"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={values.website}
                onChange={update('website')}
              />
              {errors.form && <p className="tp-form__alert" role="alert">{errors.form}</p>}
              <button type="submit" className="btn btn--primary tp-form__submit" disabled={sending}>
                {sending ? t('contactus_sending') : t('contactus_btn_send')}
                <Send size={16} strokeWidth={2} aria-hidden="true" />
              </button>
            </form>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

export default ContactUs;
